// ST-1705 -Claude- Business onboarding interest form.

import { useState, type ChangeEvent, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card } from '@nearrme/ui-web';

const BUSINESS_TYPES = ['Retail store', 'Authorized dealer', 'Service center', 'Manufacturer', 'Distributor', 'Other'];

const CUSTOMER_TILES = [
  { name: 'Western Traders', city: 'Mumbai', tone: 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-100' },
  { name: 'Powai Mart', city: 'Mumbai', tone: 'bg-sky-50 text-sky-800 dark:bg-sky-950 dark:text-sky-100' },
  { name: 'Tech Zone Bangalore', city: 'Bangalore', tone: 'bg-violet-50 text-violet-800 dark:bg-violet-950 dark:text-violet-100' },
  { name: 'Koramangala Supplies', city: 'Bangalore', tone: 'bg-amber-50 text-amber-800 dark:bg-amber-950 dark:text-amber-100' },
  { name: 'Delhi Gadgets', city: 'New Delhi', tone: 'bg-rose-50 text-rose-800 dark:bg-rose-950 dark:text-rose-100' },
  { name: 'Gujarat Electronics', city: 'Ahmedabad', tone: 'bg-cyan-50 text-cyan-800 dark:bg-cyan-950 dark:text-cyan-100' },
];

const BRAND_LOGOS = [
  { name: 'TechPro', mark: 'TP', tone: 'from-emerald-600 to-slate-900' },
  { name: 'HomeEase', mark: 'HE', tone: 'from-sky-600 to-emerald-700' },
  { name: 'GadgetWorld', mark: 'GW', tone: 'from-violet-600 to-slate-900' },
  { name: 'FreshMart', mark: 'FM', tone: 'from-lime-600 to-emerald-800' },
  { name: 'UrbanStyle', mark: 'US', tone: 'from-rose-600 to-slate-900' },
  { name: 'NutriLife', mark: 'NL', tone: 'from-amber-500 to-emerald-800' },
];

const FEEDBACK = [
  {
    quote: 'Customers should know whether the product is genuine before they travel to the store.',
    label: 'Retail partner feedback',
  },
  {
    quote: 'Warranty confidence and verified dealer visibility are more useful than another marketplace listing.',
    label: 'Manufacturer feedback',
  },
  {
    quote: 'Nearby availability helps customers decide faster and reduces unnecessary phone calls.',
    label: 'Store operator feedback',
  },
];

const HOW_IT_WORKS = [
  {
    step: '1',
    title: 'Share store details',
    body: 'Tell us your business name, city, contact details, and business type.',
  },
  {
    step: '2',
    title: 'Nearrme verifies',
    body: 'We review the store, manufacturer relationship, and warranty support path.',
  },
  {
    step: '3',
    title: 'Products go live',
    body: 'Your catalog and inventory can appear for nearby customers searching Nearrme.',
  },
];

export function BusinessSignupPage() {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    businessName: '',
    contactName: '',
    email: '',
    phone: '',
    city: '',
    businessType: BUSINESS_TYPES[0],
    message: '',
  });

  const update = (key: keyof typeof form) => (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const value = e.target.value;
    setForm((current) => {
      return { ...current, [key]: value };
    });
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    console.info('[business signup request]', form);
    setSubmitted(true);
  };

  return (
    <div className="animate-fade-in">
      <section className="bg-slate-50 dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-6xl mx-auto px-4 py-10 sm:py-14 grid lg:grid-cols-[minmax(0,1fr)_420px] gap-8 items-stretch">
        <section className="rounded-xl bg-gradient-to-br from-brand-700 via-emerald-700 to-slate-950 text-white p-7 sm:p-8 flex flex-col justify-between">
          <div>
          <p className="text-sm font-semibold text-brand-700 dark:text-brand-300 uppercase tracking-wide">
            For businesses
          </p>
          <h1 className="mt-3 text-3xl sm:text-5xl font-bold tracking-tight">
            List your store and products on Nearrme.
          </h1>
          <p className="mt-4 text-lg text-emerald-50">
            Help nearby customers find your verified store, see available products, and buy with original manufacturer warranty.
          </p>
          </div>
          <div className="mt-8 grid sm:grid-cols-3 gap-3">
            {['Store listing', 'Product catalog', 'Inventory visibility'].map((item) => (
              <div key={item} className="border border-white/15 bg-white/10 rounded-lg p-4">
                <div className="text-sm font-semibold">{item}</div>
              </div>
            ))}
          </div>
          <div className="mt-6 rounded-lg border border-white/15 bg-white/10 p-4">
            <h2 className="text-base font-bold">How it works</h2>
            <div className="mt-4 grid gap-3">
              {HOW_IT_WORKS.map((item) => (
                <div key={item.step} className="grid grid-cols-[32px_1fr] gap-3">
                  <div className="h-8 w-8 rounded-full bg-white text-brand-700 flex items-center justify-center text-sm font-black">
                    {item.step}
                  </div>
                  <div>
                    <div className="text-sm font-semibold">{item.title}</div>
                    <p className="mt-1 text-sm leading-5 text-emerald-50">{item.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Link
              to="/business/login"
              className="inline-flex justify-center items-center h-11 px-5 rounded-lg bg-slate-950 dark:bg-white text-white dark:text-slate-950 text-sm font-semibold hover:opacity-90"
            >
              Business Login
            </Link>
          </div>
        </section>

        <Card padded={true}>
          {submitted ? (
            <div className="text-center py-8">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Request received</h2>
              <p className="mt-3 text-slate-600 dark:text-slate-300">
                We will review your business details and contact you for verification.
              </p>
              <Button className="mt-6" variant="primary" onClick={() => setSubmitted(false)}>
                Add another business
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">Business signup</h2>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Submit your store details. We will verify your business before products are shown to customers.
              </p>
              <Field label="Business name" value={form.businessName} onChange={update('businessName')} required />
              <Field label="Contact name" value={form.contactName} onChange={update('contactName')} required />
              <Field
                label="Business login email"
                type="email"
                value={form.email}
                onChange={update('email')}
                helper="This can be the same email you use as a Nearrme consumer account."
                required
              />
              <Field label="Phone" type="tel" value={form.phone} onChange={update('phone')} required />
              <Field label="City" value={form.city} onChange={update('city')} required />
              <label className="block">
                <span className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Business type</span>
                <select
                  value={form.businessType}
                  onChange={update('businessType')}
                  className="w-full h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                  {BUSINESS_TYPES.map((type) => <option key={type}>{type}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Notes</span>
                <textarea
                  value={form.message}
                  onChange={update('message')}
                  rows={3}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </label>
              <Button type="submit" variant="primary" size="lg" fullWidth>
                Sign up business
              </Button>
            </form>
          )}
        </Card>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 py-10">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-slate-950 dark:text-slate-50">Manufacturer and brand network</h2>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
              Demo brands synced from SalesTrack catalog data.
            </p>
          </div>
          <Link to="/about" className="text-sm font-semibold text-brand-700 dark:text-brand-300 hover:underline">
            About Nearrme
          </Link>
        </div>
        <div className="mt-5 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {BRAND_LOGOS.map((brand) => (
            <div
              key={brand.name}
              className="min-h-24 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-3 flex flex-col items-center justify-center text-center"
            >
              <div className={`h-11 w-11 rounded-lg bg-gradient-to-br ${brand.tone} text-white flex items-center justify-center text-sm font-black tracking-wide shadow-sm`}>
                {brand.mark}
              </div>
              <div className="mt-2 text-sm font-bold text-slate-800 dark:text-slate-100">{brand.name}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 pb-10">
        <div>
          <h2 className="text-xl font-bold text-slate-950 dark:text-slate-50">Featured verified local stores</h2>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            Store examples from synced SalesTrack business data, shown separately from manufacturers.
          </p>
        </div>
        <div className="mt-5 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {CUSTOMER_TILES.map((store) => (
            <div
              key={store.name}
              className={`min-h-24 rounded-lg border border-slate-200 dark:border-slate-800 px-3 py-4 flex flex-col items-center justify-center text-center ${store.tone}`}
            >
              <div className="text-xs font-black tracking-wide uppercase">
                {store.name.split(' ').map((part) => part[0]).join('').slice(0, 3)}
              </div>
              <div className="mt-2 text-sm font-bold leading-tight">{store.name}</div>
              <div className="mt-1 text-xs opacity-75">{store.city}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 pb-12">
        <div className="grid md:grid-cols-3 gap-4">
          {FEEDBACK.map((item) => (
            <Card key={item.label} padded>
              <p className="text-sm leading-6 text-slate-700 dark:text-slate-200">"{item.quote}"</p>
              <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-brand-700 dark:text-brand-300">
                {item.label}
              </p>
            </Card>
          ))}
        </div>
      </section>
      </div>
  );
}

function Field(props: {
  label: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  type?: string;
  helper?: string;
  required?: boolean;
}) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">{props.label}</span>
      <input
        type={props.type ?? 'text'}
        required={props.required}
        value={props.value}
        onChange={props.onChange}
        className="w-full h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500"
      />
      {props.helper && (
        <span className="mt-1 block text-xs text-slate-500 dark:text-slate-400">{props.helper}</span>
      )}
    </label>
  );
}
